import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Users, MessageSquare, Lightbulb, Calendar } from "lucide-react";

// Icon name → Lucide component. Kept small on purpose — admins choose from
// this whitelist through the CMS block's `feature_cards[*].icon` string.
const ICONS = {
  users: Users,
  "message-square": MessageSquare,
  lightbulb: Lightbulb,
  calendar: Calendar,
};

// Verbatim defaults from the 8/4/2026 hardcoded version. The CMS block seed
// (backend/seed_data/content_pages.py → USER_GROUPS_DATA) mirrors this and
// the pre-commit verbatim guard enforces they stay in sync.
export const USER_GROUPS_DEFAULT_DATA = {
  breadcrumb_label: "Antera Exchange",
  breadcrumb_href: "/exchange",
  title_prefix: "Product",
  title_accent: "User Groups",
  tagline: "Share and learn best practices with the community, along with impacting future feature direction. Collaborate with other Antera users to maximize the value of your software investment.",
  feature_cards: [
    { icon: "message-square", title: "Peer Collaboration", desc: "Connect with other Antera users in your industry segment to share workflows, tips, and strategies that drive real results." },
    { icon: "lightbulb", title: "Feature Roadmap Influence", desc: "Your voice matters. User Group feedback directly shapes future product development and feature prioritization." },
    { icon: "users", title: "Expert-Led Sessions", desc: "Join facilitated discussions led by Antera product experts who share advanced tips and upcoming feature previews." },
    { icon: "calendar", title: "Regular Meetups", desc: "Scheduled virtual and in-person meetings throughout the year ensure consistent learning and networking opportunities." },
  ],
  cta_label: "Join a User Group",
  cta_href: "/contact",
};

export default function UserGroupsPage({ data }) {
  const d = { ...USER_GROUPS_DEFAULT_DATA, ...(data || {}) };
  return (
    <div data-testid="user-groups-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {d.breadcrumb_label ? (
              <Link to={d.breadcrumb_href || "/"} className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 mb-6">
                <ArrowLeft className="h-3.5 w-3.5" /> {d.breadcrumb_label}
              </Link>
            ) : null}
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {d.title_prefix} <span className="text-sky-600">{d.title_accent}</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl">
              {d.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(d.feature_cards || []).map((item, i) => {
              const Icon = ICONS[item.icon] || Users;
              return (
                <motion.div key={item.title || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.4 }} className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
          {d.cta_label ? (
            <div className="text-center mt-12">
              <Link to={d.cta_href || "/contact"}>
                <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-12 text-sm font-semibold">
                  {d.cta_label} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
